import { CONTACT_FORM_SETTINGS_KEY } from "features/Account/modules/Communications/modules/ContactForm/modules/ContactFormSettings/modules/ContactFormFields/constants/ContactFormFields.constants";
import { QUESTION_TYPE_IDS } from "features/Account/modules/Communications/modules/ContactForm/modules/QuestionForm/modules/common/QuestionFormFields/constants/QuestionFormFields.constants";
import { useAccountSectionUnlocking } from "features/Account/utils/useAccountSectionChange";
import {
  UPDATE_ADMIN_PANEL_SETTINGS,
  UPDATE_ADMIN_PANEL_SETTINGS_LISITNG_LEVEL,
} from "features/AdminPanel/constants/AdminPanel.api";
import { useActionFieldModalWrapper } from "features/AdminPanel/modules/DraggableTableWrapper/modules/ActionsField/modules/ActionFieldsModalWrapper/utils/useActionFieldModalWrapper";
import { useAdminPanelStore } from "features/AdminPanel/store/useAdminPanelStore";
import { checkIsStringEmpty } from "features/Common/modules/DataTypes/Strings.utils";
import { is_empty } from "features/Common/utils/common.utils";
import { STATUS_VISIBLE_TIME } from "features/ManageOfferings/modules/SavingStatus/constants/SavingStatus.constants";
import { useSavingStatus } from "features/ManageOfferings/modules/SavingStatus/utils/useSavingStatus";
import { useManageOfferingsStore } from "features/ManageOfferings/store/useManageOfferingsStore";
import { useRef } from "react";
import { addQuestionsValidations } from "schedule-v2/commonPages/Pricing.utils";
import { logError } from "utils/error";
import { notification_color_keys, useNotifications } from "utils/hooks";
import {
  LISTING_DEFAULT_QUESTIONS_KEYS,
  LISTING_QUESTIONS_FORM_SETTINGS_KEY,
} from "../constants/QuestionFormList.constants";
import {
  getFeatureKeyAndValueForDefaultTypeQuestion,
  isDefaultTypeQuestion,
} from "./QuestionsPage.utils";

export const useQuestionForm = ({ listingUuid }) => {
  const isListingLevelQuestion = !checkIsStringEmpty(listingUuid);
  const timerRef = useRef(null);
  const { setErrorStatus, setSaved, setSaving, hideStatus } = useSavingStatus();
  const { notify } = useNotifications();
  const reorderTimeoutRef = useRef(null);
  const { handleSectionChange } = useAccountSectionUnlocking();
  const sectionConfig = useAdminPanelStore((state) => state.sectionConfig);

  const { isActionFieldsModalOpen, closeActionFieldModal } =
    useActionFieldModalWrapper();

  const listingFormQuestionsList = useManageOfferingsStore(
    (state) => state.listingFormQuestionsList
  );
  const listingFormQuestionsListLoading = useManageOfferingsStore(
    (state) => state.listingFormQuestionsListLoading
  );

  const setListingFormQuestionsList = useManageOfferingsStore(
    (state) => state.setListingFormQuestionsList
  );

  const getDefaultUpdatedQuestionValues = (finalData) => {
    try {
      return finalData.map((question) => {
        // Set all other keys key
        if (!checkIsStringEmpty(sectionConfig?.[question.uuid]?.value)) {
          question.question = sectionConfig[question.uuid]?.value;
        }

        // Set ENABLE_OTP_VERIFICATION key
        if (
          question.uuid == LISTING_DEFAULT_QUESTIONS_KEYS.PHONE &&
          question?.enable_otp_verification != undefined
        ) {
          question.enable_otp_verification =
            sectionConfig[
              LISTING_DEFAULT_QUESTIONS_KEYS.ENABLE_OTP_VERIFICATION
            ]?.value;
        }
        return question;
      });
    } catch (error) {
      return finalData;
    }
  };

  const getDefaultPreBookingQuestions = () => {
    const questions = [
      {
        _id: LISTING_DEFAULT_QUESTIONS_KEYS.NAME,
        uuid: LISTING_DEFAULT_QUESTIONS_KEYS.NAME,
        question: "Name",
        type: QUESTION_TYPE_IDS.SHORT_TEXT,
        priority: 0,
        question_placement: 1,
        is_mandatory: true,
        isDragDisabled: true,
        is_default: true,
      },
      {
        _id: LISTING_DEFAULT_QUESTIONS_KEYS.EMAIL,
        uuid: LISTING_DEFAULT_QUESTIONS_KEYS.EMAIL,
        question: "Email",
        type: QUESTION_TYPE_IDS.EMAIL,
        priority: 0,
        question_placement: 1,
        is_mandatory: true,
        isDragDisabled: true,
        is_default: true,
      },
      {
        _id: LISTING_DEFAULT_QUESTIONS_KEYS.PHONE,
        uuid: LISTING_DEFAULT_QUESTIONS_KEYS.PHONE,
        question: "Phone Number",
        type: QUESTION_TYPE_IDS.PHONE_NUMBER,
        priority: 0,
        question_placement: 1,
        is_mandatory: true,
        isDragDisabled: true,
        is_default: true,
        enable_otp_verification: false,
      },
    ];

    return questions;
  };

  const transformListingFormQuestionsList = async (data = []) => {
    try {
      const transformedData = data.map((item) => ({
        ...item,
        _id: item.uuid,
        isDragDisabled: item?.is_default,
      }));
      let finalData = [...getDefaultPreBookingQuestions(), ...transformedData];
      getDefaultUpdatedQuestionValues(finalData);
      setListingFormQuestionsList(finalData);
      return finalData;
    } catch (error) {
      logError({
        error,
        when: "calling transformListingFormQuestionsList",
        occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
      });
      return data;
    }
  };

  /**
   * Handles the reordering of questions, updates UI immediately, and throttles the API call.
   */
  const handleReordering = ({ reorderedList, reorderAPI, actionType }) => {
    const previousState = listingFormQuestionsList;
    if (is_empty(reorderedList)) return;
    setSaving({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });

    const mergedList = Array.from(
      new Map(
        [...reorderedList, ...listingFormQuestionsList].map((item) => [
          item.uuid,
          item,
        ])
      ).values()
    );

    const payload = {
      ...(listingUuid ? { listing_uuid: listingUuid } : {}),
      reorder: mergedList
        .filter((question) => !isDefaultTypeQuestion({ question }))
        .map((question, index) => ({
          question_uuid: question.uuid,
          priority: index,
          name: question.question,
        })),
      ...(actionType ? { action_type: actionType } : {}),
    };

    // Optimistic UI update
    setListingFormQuestionsList(mergedList);

    if (reorderTimeoutRef.current) {
      clearTimeout(reorderTimeoutRef.current);
    }

    reorderTimeoutRef.current = setTimeout(() => {
      reorderAPI(payload, {
        onSuccess: () => {
          setSaved({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
        },
        onError: (error) => {
          setErrorStatus({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
          setListingFormQuestionsList(previousState);
          logError({
            error,
            when: "calling handleReordering",
            occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
          });
        },
      });
    }, 500);
  };

  /**
   * Handles the addition of a new question to the contact form.
   *
   * @param {Object} params - Parameters for adding a question.
   * @param {Object} params.formData - The form data containing question details.
   * @param {Object} params.form - The form instance for managing form state.
   * @param {Function} [params.onSuccess] - Callback to execute after a successful addition.
   */
  const handleAddQuestion = async ({
    formState,
    onSuccess = () => {},
    addAPI,
    actionType,
  }) => {
    if (is_empty(formState)) return;
    const { questionObj } = formState;

    if (!addQuestionsValidations(questionObj, notify)) {
      return;
    }

    const lastQuestion =
      listingFormQuestionsList[listingFormQuestionsList.length - 1];

    const payload = {
      listing_uuid: listingUuid,
      ...questionObj,
      priority: (lastQuestion?.priority ?? 0) + 1,
      ...(actionType ? { action_type: actionType } : {}),
    };

    try {
      addAPI(payload, {
        onSuccess: (data) => {
          setSaved({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
          const questionId = data?.uuid;
          notify(
            "Question added successfully",
            notification_color_keys.success
          );
          setListingFormQuestionsList([
            ...listingFormQuestionsList,
            { ...payload, uuid: questionId, _id: questionId },
          ]);
          onSuccess();
        },
        onError: (error) => {
          setErrorStatus({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
          logError({
            error,
            when: "calling handleAddQuestion",
            occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
          });
        },
      });
    } catch (error) {
      logError({
        error,
        when: "calling handleAddQuestion",
        occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
      });
    }
  };

  /**
   * Handles the deletion of a question from the contact form questions list.
   *
   * @param {Object} params - Parameters containing the questionId to be deleted.
   * @param {string} params.questionId - The UUID of the question to delete.
   */
  const handleDeleteQuestion = ({ questionId, deleteAPI, actionType }) => {
    if (!questionId) return;
    setSaving({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
    notify("Deleting Question", notification_color_keys.info);

    const payload = {
      question_uuid: questionId,
      ...(listingUuid ? { listing_uuid: listingUuid } : {}),
      ...(actionType ? { action_type: actionType } : {}),
    };

    try {
      deleteAPI(payload, {
        onSuccess: () => {
          setSaved({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
          notify(
            "Question deleted successfully",
            notification_color_keys.success
          );
          const filteredQuestionsList = listingFormQuestionsList?.filter(
            (question) => question.uuid !== questionId
          );
          setListingFormQuestionsList(filteredQuestionsList);
        },
        onError: (error) => {
          setErrorStatus({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
          notify("Failed Deleting Question", notification_color_keys.error);
          logError({
            error,
            when: "calling handleReordering",
            occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
          });
        },
      });
      return;
    } catch (error) {
      logError({
        error,
        when: "calling handleDeleteQuestion",
        occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
      });
    }
  };

  /**
   * Handles editing a question in the contact form.
   *
   * @param {Object} params - Parameters for editing a question.
   * @param {Object} params.formData - The form data containing updated question details.
   * @param {Object} params.form - The form instance for managing form state.
   * @param {Function} [params.onSuccess] - Callback to execute after a successful edit.
   */
  const handleEditQuestion = async ({
    formData: data,
    form,
    onSuccess = () => {},
    editAPI,
    actionType,
  }) => {
    const formData = data?.questionObj;
    const formState = form.getState();
    const questionId = formData?.question_uuid;
    const isDirty = formState.dirty;

    if (!questionId || !isDirty) return;
    setSaving({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });

    const initialValues = formState.initialValues?.questionObj;
    if (!addQuestionsValidations(initialValues, notify)) return;

    const dirtyValues = Object.keys(formData).reduce((acc, key) => {
      if (formData[key] !== initialValues[key]) {
        acc[key] = formData[key];
      }
      return acc;
    }, {});

    // If Default Question Types then edit through admin panel api
    if (isDefaultTypeQuestion({ question: formData })) {
      return handleEditDefaultQuestions({
        question: formData,
        value: dirtyValues,
        onSuccess,
        actionType,
      });
    }

    const payload = {
      question_uuid: questionId,
      ...(listingUuid ? { listing_uuid: listingUuid } : {}),
      updated_values: {
        ...dirtyValues,
      },
      ...(actionType ? { action_type: actionType } : {}),
    };
    try {
      editAPI(payload, {
        onSuccess: () => {
          setSaved({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
          notify(
            "Question edited successfully",
            notification_color_keys.success
          );

          const updatedQuestionsList = listingFormQuestionsList.map(
            (question) =>
              question.uuid === questionId
                ? { ...question, ...dirtyValues }
                : question
          );
          setListingFormQuestionsList(updatedQuestionsList);
        },
        onError: (error) => {
          setErrorStatus({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
          logError({
            error,
            when: "calling handleReordering",
            occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
          });
        },
      });

      onSuccess();
      if (isActionFieldsModalOpen) closeActionFieldModal();
    } catch (error) {
      logError({
        error,
        when: "calling handleEditQuestion",
        occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
      });
    }
  };

  const handleEditDefaultQuestions = ({
    question,
    value,
    onSuccess,
    actionType,
  }) => {
    const payload = {
      listing_uuid: listingUuid,
      ...getFeatureKeyAndValueForDefaultTypeQuestion({ question, value }),
      ...(actionType ? { action_type: actionType } : {}),
    };
    if (!payload?.feature_key) return;
    const questionId = question?.question_uuid;
    handleSectionChange({
      endPoint: isListingLevelQuestion
        ? UPDATE_ADMIN_PANEL_SETTINGS_LISITNG_LEVEL
        : UPDATE_ADMIN_PANEL_SETTINGS,
      payload,
    })
      .then(() => {
        const updatedQuestionsList = listingFormQuestionsList.map((question) =>
          question.uuid === questionId ? { ...question, ...value } : question
        );
        setListingFormQuestionsList(updatedQuestionsList);
        setSaved({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
        onSuccess();
        if (isActionFieldsModalOpen) closeActionFieldModal();
      })
      .catch((error) => {
        setErrorStatus({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
        notify("Failed Editing Question", notification_color_keys.error);
        logError({
          error,
          when: "calling handleEditDefaultQuestions",
          occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
        });
      });

    return;
  };

  const handleToggleQuestionMandatory = async ({
    questionId,
    value,
    editAPI,
    actionType,
  }) => {
    if (!questionId) return;
    setSaving({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
    if (timerRef.current) clearTimeout(timerRef.current);
    const payload = {
      question_uuid: questionId,
      ...(listingUuid ? { listing_uuid: listingUuid } : {}),
      updated_values: {
        is_mandatory: value,
      },
      ...(actionType ? { action_type: actionType } : {}),
    };
    try {
      const previousState = listingFormQuestionsList;
      const updatedQuestionsList = listingFormQuestionsList.map((question) =>
        question.uuid === questionId
          ? {
              ...question,
              is_mandatory: value,
              ...(question?.multi_questions
                ? {
                    multi_questions: question.multi_questions.map((q) => ({
                      ...q,
                      is_mandatory: value,
                    })),
                  }
                : {}),
            }
          : question
      );
      setListingFormQuestionsList(updatedQuestionsList);
      editAPI(payload, {
        onSuccess: () => {
          setSaved({ featureKey: CONTACT_FORM_SETTINGS_KEY });
          setSaved({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
        },
        onError: (error) => {
          setListingFormQuestionsList(previousState);
          setErrorStatus({ featureKey: LISTING_QUESTIONS_FORM_SETTINGS_KEY });
          logError({
            error,
            when: "calling handleReordering",
            occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
          });
        },
      });
    } catch (error) {
      setErrorStatus({ featureKey: CONTACT_FORM_SETTINGS_KEY });

      logError({
        error,
        when: "calling handleToggleQuestionMandatory",
        occuredAt: "src/ui/pages/QuestionsPage/utils/useQuestionsForm.ts",
      });
    } finally {
      timerRef.current = setTimeout(() => {
        hideStatus({ featureKey: CONTACT_FORM_SETTINGS_KEY });
      }, STATUS_VISIBLE_TIME);
    }
  };

  return {
    handleReordering,
    handleToggleQuestionMandatory,
    handleAddQuestion,
    handleEditQuestion,
    handleDeleteQuestion,
    listingFormQuestionsList,
    listingFormQuestionsListLoading,
    transformListingFormQuestionsList,
  };
};
